import { createSlice } from '@reduxjs/toolkit';

const initialFormData = {
  name: '',
  thread_access_level: 'PUBLIC',
  tags: [],
  userAccess: [],
};

const initialFiles = {
  pdfFiles: [],
  wordFiles: [],
  pptFiles: [],
  textFiles: [],
  excelFiles: [],
  csvFiles: [],
};

const initialState = {
  // Listing state
  threads: [],
  total: 0,
  page: 1,
  size: 25,
  search: '',
  filterType: 'ALL', // 'ALL' | 'PUBLIC' | 'PRIVATE'
  slrProjects: [],
  selectedSlrProjects: [],

  // View state
  showForm: false,
  showRefLibrary: false,
  editingThread: null,
  threadDetails: null,

  // Form state
  formData: initialFormData,
  selectedUsers: [],
  // NOTE: `files` (File objects) are NOT stored in Redux because they are
  // non-serializable. They stay in local useState inside the component.
};

const unstructuredThreadSlice = createSlice({
  name: 'unstructuredThread',
  initialState,
  reducers: {

    // ── Listing ────────────────────────────────────────────
    setThreads(state, action) {
      state.threads = action.payload;
    },

    setTotal(state, action) {
      state.total = action.payload;
    },

    setPage(state, action) {
      state.page = action.payload;
    },

    setSize(state, action) {
      state.size = action.payload;
    },

    setSearch(state, action) {
      state.search = action.payload;
    },

    setFilterType(state, action) {
      state.filterType = action.payload;
    },

    setSlrProjects(state, action) {
      state.slrProjects = action.payload;
    },

    setSelectedSlrProjects(state, action) {
      state.selectedSlrProjects = action.payload;
    },

    // ── View ───────────────────────────────────────────────
    setShowForm(state, action) {
      state.showForm = action.payload;
    },

    setShowRefLibrary(state, action) {
      state.showRefLibrary = action.payload;
    },

    setEditingThread(state, action) {
      state.editingThread = action.payload;
    },

    setThreadDetails(state, action) {
      state.threadDetails = action.payload;
    },

    // ── Form ───────────────────────────────────────────────
    setFormData(state, action) {
      state.formData = action.payload;
    },

    setSelectedUsers(state, action) {
      state.selectedUsers = action.payload;
    },

    // ── Bulk: populate form when editing ───────────────────
    loadEditingThread(state, action) {
      const thread = action.payload;
      state.editingThread = thread;
      state.showForm = true;
      state.threadDetails = null;
      state.formData = {
        name: thread.name || '',
        thread_access_level: thread.thread_access_level || 'PUBLIC',
        tags: thread.tags || [],
        userAccess: thread.user_access || [],
      };
      state.selectedUsers =
        thread.thread_access_level === 'PRIVATE'
          ? (thread.user_access || []).map(u => u.user_id)
          : [];
    },

    // ── Open thread details ────────────────────────────────
    openThreadDetails(state) {
      state.showForm = false;
      state.editingThread = null;
      // threadDetails will be set after API call
    },

    // ── Reset form only ────────────────────────────────────
    resetForm(state) {
      state.formData = {
        name: '',
        thread_access_level: 'PRIVATE',
        tags: ['research paper', 'clinical guidelines'],
        userAccess: [],
      };
      state.selectedUsers = [];
      state.editingThread = null;
      state.showForm = false;
    },

    // ── Full reset ─────────────────────────────────────────
    resetUnstructuredThreadState() {
      return initialState;
    },
  },
});

export const {
  setThreads,
  setTotal,
  setPage,
  setSize,
  setSearch,
  setFilterType,
  setSlrProjects,
  setSelectedSlrProjects,
  setShowForm,
  setShowRefLibrary,
  setEditingThread,
  setThreadDetails,
  setFormData,
  setSelectedUsers,
  loadEditingThread,
  openThreadDetails,
  resetForm,
  resetUnstructuredThreadState,
} = unstructuredThreadSlice.actions;

export default unstructuredThreadSlice.reducer;